import {
  effect,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  Service,
} from '@toeverything/infra';
import { combineLatest, map, Observable, type Subscription, tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type {
  DocPermissionActions,
  GuardStore,
  WorkspacePermissionActions,
} from '../stores/guard';
import { getNoDocPermissions } from '../stores/guard';
import { workspacePermissionEventMayChangeRole } from '../utils/workspace-permission-events';
import type { WorkspacePermissionService } from './permission';

export class GuardService extends Service {
  private readonly permissionsChangedSubscription?: Subscription;
  private readonly graphAdvancedSubscription?: Subscription;
  private readonly expiredGrantsSubscription: Subscription;
  /** Pages with a live `can$` subscriber, counted per subscription. */
  private readonly watchedDocIds = new Map<string, number>();
  private readonly docPermissionLoads = new Map<
    string,
    Promise<Record<DocPermissionActions, boolean>>
  >();

  constructor(
    private readonly guardStore: GuardStore,
    private readonly workspaceService: WorkspaceService,
    private readonly workspacePermissionService: WorkspacePermissionService
  ) {
    super();
    this.permissionsChangedSubscription = this.subscribeToPermissionEvents();
    this.graphAdvancedSubscription = this.subscribeToGraphAdvanced();
    this.expiredGrantsSubscription = this.subscribeToExpiredGrants();
    void this.initialize();
  }

  /**
   * An expired offline grant must lock the doc at once, including docs that
   * are open right now: replace their last loaded permissions with deny.
   * A renewed signed grant overrides this through the cached permissions.
   */
  private subscribeToExpiredGrants(): Subscription {
    return this.guardStore.watchExpiredDocIds().subscribe(docIds => {
      const stale = docIds.filter(docId => {
        const loaded = this.docPermissions$.value[docId];
        return loaded && Object.values(loaded).some(Boolean);
      });
      if (stale.length === 0) return;
      const next = { ...this.docPermissions$.value };
      for (const docId of stale) next[docId] = getNoDocPermissions();
      this.docPermissions$.next(next);
    });
  }

  private async initialize(): Promise<void> {
    await this.guardStore.hydrateLocalPermissionSnapshot();
    if (
      this.workspaceService.workspace.flavour !== 'local' &&
      !this.workspaceService.workspace.openOptions.isSharedMode &&
      !this.workspaceService.workspace.openOptions.externalDocAccess
    ) {
      await this.loadWorkspacePermission().catch(() => undefined);
    }
  }

  private subscribeToPermissionEvents() {
    const permissionsChanged$ =
      this.workspaceService.workspace.engine.permissionsChanged$;
    if (!permissionsChanged$) {
      return undefined;
    }

    return permissionsChanged$.subscribe(event => {
      const roleMayChange = workspacePermissionEventMayChangeRole(
        event.changeType
      );
      if (roleMayChange) {
        this.revalidateCachedPermissions({ forceWorkspace: true });
      }
      void this.guardStore
        .applyPermissionEvent(event)
        .then(({ full, staleDocIds }) => {
          if (!full) return this.refreshLoadedDocPermissions(staleDocIds);
          this.revalidateAfterFullStale({
            workspace: !roleMayChange,
            docIds: event.affectedDocIds ?? [],
          });
          return undefined;
        })
        .catch(() => undefined);
    });
  }

  private subscribeToGraphAdvanced() {
    const graphAdvanced$ =
      this.workspaceService.workspace.engine.permissionGraphAdvanced$;
    if (!graphAdvanced$) {
      return undefined;
    }

    return graphAdvanced$.subscribe(event => {
      void this.guardStore
        .applyGraphAdvanced(event)
        .then(({ full }) => {
          if (full) {
            this.revalidateAfterFullStale({ workspace: true, docIds: [] });
          }
        })
        .catch(() => undefined);
    });
  }

  /**
   * Every grant went stale: reload the role, the named pages and every page
   * someone watches right now, which otherwise keeps its old online answer.
   * Other pages renew when they are asked for again.
   */
  private revalidateAfterFullStale({
    workspace,
    docIds,
  }: {
    workspace: boolean;
    docIds: string[];
  }) {
    this.revalidateCachedPermissions({
      forceWorkspace: workspace,
      docIds: [...docIds, ...this.watchedDocIds.keys()],
    });
  }

  /**
   * Only pages shown with online permissions need a request now; every
   * other stale grant is fetched when its page asks for it.
   */
  private refreshLoadedDocPermissions(docIds: string[]): Promise<void> {
    if (this.workspaceService.workspace.openOptions.externalDocAccess) {
      return Promise.resolve();
    }
    const loaded = this.docPermissions$.value;
    return this.refreshDocPermissions(docIds.filter(docId => docId in loaded));
  }

  private readonly workspacePermissions$ = new LiveData<
    Partial<Record<WorkspacePermissionActions, boolean>>
  >({});

  private readonly docPermissions$ = new LiveData<
    Record<string, Partial<Record<DocPermissionActions, boolean>>>
  >({});

  private readonly isOwner$ = LiveData.computed(get => {
    const isOwner = get(this.workspacePermissionService.permission.isOwner$);
    return isOwner ?? null;
  });

  /**
   * @example
   * ```ts
   * guardService.can$('Workspace_Properties_Update');
   * guardService.can$('Doc_Update', docId);
   * ```
   *
   * @returns LiveData<boolean | undefined> the value is undefined if the permission is loading
   */
  can$<T extends WorkspacePermissionActions | DocPermissionActions>(
    action: T,
    ...args: T extends DocPermissionActions ? [string] : []
  ): LiveData<boolean | undefined> {
    const docId = args[0];
    const externalPermission = this.getExternalDocPermission(action, docId);
    if (externalPermission !== undefined) {
      return new LiveData<boolean | undefined>(externalPermission);
    }
    if (this.workspaceService.workspace.flavour === 'local') {
      return new LiveData<boolean | undefined>(true);
    }
    return LiveData.from(
      new Observable(subscriber => {
        let prev: boolean | undefined = undefined;

        const subscription = combineLatest([
          (docId
            ? combineLatest([
                this.docPermissions$.pipe(
                  map(permissions => permissions[docId] ?? {})
                ),
                this.guardStore.watchCachedDocPermissions(docId).asObservable(),
              ]).pipe(
                map(([online, cached]) => ({
                  ...online,
                  ...(cached ?? {}),
                }))
              )
            : this.workspacePermissions$.asObservable()) as Observable<
            Record<string, boolean>
          >,
          this.isOwner$,
        ]).subscribe(([permissions, isOwner]) => {
          if (!docId && isOwner) {
            return subscriber.next(true);
          }
          const current = permissions[action] ?? undefined;
          // While workspace role is still loading (null), don't emit
          // a definitive workspace false — the user might be Owner.
          // For doc permissions, a loaded doc-specific cache value is already
          // the backend-computed answer and can be used while offline.
          if (isOwner === null) {
            if (docId) {
              if (current !== prev) {
                prev = current;
                subscriber.next(current);
              }
              return;
            }
            if (prev !== undefined) {
              prev = undefined;
              subscriber.next(undefined);
            }
            return;
          }
          if (current !== prev) {
            prev = current;
            subscriber.next(current);
          }
        });

        if (
          docId &&
          this.workspaceService.workspace.flavour !== 'local' &&
          !this.workspaceService.workspace.openOptions.isSharedMode &&
          !this.workspaceService.workspace.openOptions.externalDocAccess
        ) {
          void this.loadDocPermission(docId).catch(() => undefined);
        }
        if (docId) {
          this.watchedDocIds.set(
            docId,
            (this.watchedDocIds.get(docId) ?? 0) + 1
          );
        }

        return () => {
          subscription.unsubscribe();
          if (!docId) return;
          const watchers = (this.watchedDocIds.get(docId) ?? 1) - 1;
          if (watchers > 0) this.watchedDocIds.set(docId, watchers);
          else this.watchedDocIds.delete(docId);
        };
      }),
      undefined
    );
  }

  async can<T extends WorkspacePermissionActions | DocPermissionActions>(
    action: T,
    ...args: T extends DocPermissionActions ? [string] : []
  ): Promise<boolean> {
    const docId = args[0];
    const externalPermission = this.getExternalDocPermission(action, docId);
    if (externalPermission !== undefined) {
      return externalPermission;
    }
    if (this.workspaceService.workspace.flavour === 'local') {
      return true;
    }

    if (docId) {
      const permissions = await this.loadDocPermission(docId);
      return permissions[action as keyof typeof permissions] ?? false;
    }

    if (this.isOwner$.value === null) {
      await this.workspacePermissionService.permission.waitForRevalidation();
    }

    if (this.isOwner$.value === true) {
      return true;
    }

    const permissions = await (docId
      ? this.loadDocPermission(docId)
      : this.loadWorkspacePermission());

    return permissions[action as keyof typeof permissions] ?? false;
  }

  revalidateCan<T extends WorkspacePermissionActions | DocPermissionActions>(
    _action: T,
    ...args: T extends DocPermissionActions ? [string] : []
  ) {
    if (this.workspaceService.workspace.openOptions.externalDocAccess) {
      return;
    }
    // revalidate workspace permission if it's not initialized
    if (this.isOwner$.value === null) {
      this.workspacePermissionService.permission.revalidate();
    }

    const docId = args[0];
    if (!docId && this.isOwner$.value === true) {
      // Owner workspace permissions are not overridable.
      return;
    }

    // revalidate permission
    if (docId) {
      this.revalidateDocPermission(docId);
    } else {
      this.revalidateWorkspacePermission();
    }
  }

  revalidateCachedPermissions(options?: {
    forceWorkspace?: boolean;
    docIds?: string[];
  }) {
    if (this.workspaceService.workspace.openOptions.externalDocAccess) {
      return;
    }
    if (this.isOwner$.value === null) {
      this.workspacePermissionService.permission.revalidate();
    }

    if (options?.forceWorkspace || this.isOwner$.value !== true) {
      this.revalidateWorkspacePermission();
    }

    this.revalidateDocPermissions(options?.docIds ?? []);
  }

  getCachedDocPermission(
    action: DocPermissionActions,
    docId: string
  ): boolean | undefined {
    return (
      this.docPermissions$.value[docId]?.[action] ??
      this.guardStore.getCachedDocPermissions(docId)?.[action]
    );
  }

  private readonly revalidateWorkspacePermission = effect(
    exhaustMapWithTrailing(() =>
      fromPromise(() => this.guardStore.getWorkspacePermissions()).pipe(
        tap(permissions => {
          this.workspacePermissions$.next(permissions);
        })
      )
    )
  );

  private revalidateDocPermission(docId: string) {
    this.revalidateDocPermissions([docId]);
  }

  private revalidateDocPermissions(docIds: string[]) {
    const ids = Array.from(new Set(docIds.filter(Boolean)));
    void this.applyCachedDocPermissions(ids);
    void this.refreshDocPermissions(ids);
  }

  private async refreshDocPermissions(docIds: string[]): Promise<void> {
    const ids = Array.from(new Set(docIds.filter(Boolean)));
    if (ids.length === 0) {
      return;
    }

    const permissionsByDocId = await this.guardStore.getDocPermissionsBatch(
      ids,
      { force: true }
    );
    this.docPermissions$.next({
      ...this.docPermissions$.value,
      ...permissionsByDocId,
    });
  }

  private readonly loadWorkspacePermission = async () => {
    if (this.workspaceService.workspace.flavour === 'local') {
      return {} as Record<WorkspacePermissionActions, boolean>;
    }
    if (
      this.workspaceService.workspace.openOptions.isSharedMode ||
      this.workspaceService.workspace.openOptions.externalDocAccess
    ) {
      return {};
    }
    const permissions = await this.guardStore.getWorkspacePermissions();
    this.workspacePermissions$.next(permissions);
    return permissions;
  };

  private getExternalDocPermission(
    action: WorkspacePermissionActions | DocPermissionActions,
    docId?: string
  ): boolean | undefined {
    const access =
      this.workspaceService.workspace.openOptions.externalDocAccess;
    if (!access) return undefined;
    if (!docId || docId !== access.docId) return false;

    const permissions = getNoDocPermissions();
    permissions.Doc_Read = true;
    permissions.Doc_Properties_Read = true;
    permissions.Doc_Comments_Read = true;
    if (access.role === 'Editor') {
      permissions.Doc_Update = true;
      permissions.Doc_Properties_Update = true;
      permissions.Doc_Comments_Create = true;
      permissions.Doc_Comments_Resolve = true;
    }
    return permissions[action as DocPermissionActions] ?? false;
  }

  private readonly loadDocPermission = async (docId: string) => {
    const activeLoad = this.docPermissionLoads.get(docId);
    if (activeLoad) {
      return activeLoad;
    }

    const load = this.loadDocPermissionOnce(docId).finally(() => {
      if (this.docPermissionLoads.get(docId) === load) {
        this.docPermissionLoads.delete(docId);
      }
    });
    this.docPermissionLoads.set(docId, load);
    return load;
  };

  private readonly loadDocPermissionOnce = async (
    docId: string
  ): Promise<Record<DocPermissionActions, boolean>> => {
    if (this.workspaceService.workspace.flavour === 'local') {
      return {} as Record<DocPermissionActions, boolean>;
    }
    if (
      this.workspaceService.workspace.openOptions.isSharedMode ||
      this.workspaceService.workspace.openOptions.externalDocAccess
    ) {
      return {} as Record<DocPermissionActions, boolean>;
    }
    await this.guardStore.hydrateLocalPermissionSnapshot();
    const cached = this.guardStore.getCachedDocPermissions(docId);
    if (cached) {
      this.docPermissions$.next({
        ...this.docPermissions$.value,
        [docId]: cached,
      });
      return cached;
    }
    const permissions = await this.guardStore.getDocPermissions(docId);
    this.docPermissions$.next({
      ...this.docPermissions$.value,
      [docId]: permissions,
    });
    return permissions;
  };

  private readonly applyCachedDocPermissions = async (docIds: string[]) => {
    await this.guardStore.hydrateLocalPermissionSnapshot();
    const nextPermissions = { ...this.docPermissions$.value };
    for (const docId of docIds) {
      const cached = this.guardStore.getCachedDocPermissions(docId);
      if (cached) {
        nextPermissions[docId] = cached;
      }
    }
    this.docPermissions$.next(nextPermissions);
  };

  override dispose() {
    this.permissionsChangedSubscription?.unsubscribe();
    this.graphAdvancedSubscription?.unsubscribe();
    this.expiredGrantsSubscription.unsubscribe();
    this.revalidateWorkspacePermission.unsubscribe();
  }
}
